/* CASH REPORT — Templates 1, 2 and 3 as screens.

   Max, 2026-10-08: "template 1,2,3 là cái sẽ import vào á có thể chọn điền tay hoặc import file".
   The three templates are the INPUT; the three reports are what the portal builds from it. A
   Preparer can download the template, fill it in Excel and import it, or fill the same fields here —
   both make a version the same way (`/entry` and `/upload` share one save on the server) and go
   through the same Data Check. In Cash Report Import the form IS the view: it opens on the version
   in the flow, so an imported file arrives in it ready to be corrected.

   The layout is Tuan's own template tab, field by field, and the check column works as he wrote it
   in Excel: it says OK or the difference while you type, so a figure that does not add up is seen
   before Save rather than after. Numbers are kept as typed (commas allowed) and read by the server
   exactly as it reads a cell. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Save, X } from 'lucide-react';
import { money } from './cashUi';
import { Field1, Field2Head, SetupTick, FileCell, TopTable } from './CashT1';
import { SheetTable, SheetTitle, evalSheet, monthLabel } from './CashPlanSheet';

const toNum = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const x = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(x) ? x : NaN;
};
const r2 = (v) => Math.round(v * 100) / 100;
const asText = (v) => (v === null || v === undefined ? '' : String(v));

function NumBox({ value, onChange, label, readOnly, placeholder }) {
  const bad = Number.isNaN(toNum(value));
  return (
    <input className={`cf-num${bad ? ' bad' : ''}`} type="text" inputMode="decimal" value={value} readOnly={readOnly}
      aria-label={label} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  );
}
/* The form is what the Import screen shows, so it is open all the time: Save and Discard come alive
   once something has been changed, and a form that holds no version yet (`fresh`) can be saved as it
   stands. `useDirty` compares what Save would send with what the form opened with. */
function useDirty(payload, onDirty) {
  const first = useRef(null);
  const now = JSON.stringify(payload);
  if (first.current === null) first.current = now;
  const dirty = now !== first.current;
  /* Told upward too: the panel's toolbar offers Cancel editing beside Import while there is
     something unsaved, and holds Import back until it is saved or cancelled. */
  useEffect(() => { if (onDirty) onDirty(dirty); }, [dirty, onDirty]);
  return dirty;
}
function Bar({ t, busy, onSave, onCancel, dirty, fresh }) {
  return (
    <div className="cf-bar">
      <button type="button" className="pa-btn approve" disabled={busy || (!dirty && !fresh)} onClick={onSave}><Save size={14} /> {t('cashf.save')}</button>
      <button type="button" className="pa-btn" disabled={busy || !dirty} onClick={onCancel}><X size={14} /> {t('cashf.cancel')}</button>
      <span className="pa-muted">{dirty || fresh ? t('cashf.saveHint') : t('cashf.noChanges')}</span>
    </div>
  );
}

/* ---- Template 1 ------------------------------------------------------------------------------
   Tuan's sheet (the layout and its reasons are in CashT1.jsx). BALANCES ONLY since 2026-10-29: what the
   Preparer types is each account's Closing balance per the bank, then Fields 5 and 6. The Restricted /
   Fixed Deposit / Available columns and the whole of Field 1 are worked out while typing, and the
   server works them out again from the same figures, so what is drawn here is what will be stored. */
export function WeeklyForm({ t, form, busy, onSave, onCancel, onAttach, onDirty, filesFor, fileLink }) {
  const cur = form.current;
  const base = form.base || 'SGD';
  const [rows, setRows] = useState(() => form.accounts.map((a) => {
    const r = cur?.rows?.find((x) => x.account_id === a.account_id);
    return { ...a, closing: asText(r?.closing_stmt) };
  }));
  const [tops, setTops] = useState(() => ['spend', 'payable'].flatMap((kind) => [1, 2, 3].map((rank) => {
    const x = cur?.tops?.find((y) => y.kind === kind && Number(y.rank_no) === rank);
    return { kind, rank_no: rank, category_code: asText(x?.category_code), memo: asText(x?.memo), amount_sgd: asText(x?.amount_sgd) };
  })));
  const setRow = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const setTop = (kind) => (rank, k, v) => setTops((ts) => ts.map((x) => (x.kind === kind && x.rank_no === rank ? { ...x, [k]: v } : x)));

  // ---- the worked-out figures, as Tuan's formulas have them ----
  /* The week's rate is Set up's alone (synced from NetSuite) and is never typed here — Max,
     2026-10-13. An account whose currency has no rate for the week leaves Field 1 unworked-out and the
     Data Check names the currency; the cure is in Set up › Exchange rates, not on this form. */
  const setupRate = (ccy) => (ccy === base ? 1 : (form.rates?.[ccy] != null ? Number(form.rates[ccy]) : null));
  const calc = rows.map((r) => {
    const closing = toNum(r.closing);
    const bad = Number.isNaN(closing);
    const c = bad ? null : closing;
    const restricted = c == null ? null : (r.restricted ? c : 0);
    const fd = c == null ? null : (r.fixed_deposit && !r.restricted ? c : 0);
    return { bad, closing: c, restricted, fd, available: c == null ? null : r2(c - restricted - fd), rate: setupRate(r.currency) };
  });
  const totals = (() => {
    if (calc.some((c) => c.bad || c.closing == null || c.rate == null)) return null;
    const tt = calc.reduce((s, c) => ({ closing: s.closing + c.closing * c.rate, restricted: s.restricted + c.restricted * c.rate,
      fixed_deposit: s.fixed_deposit + c.fd * c.rate }), { closing: 0, restricted: 0, fixed_deposit: 0 });
    const out = Object.fromEntries(Object.entries(tt).map(([k, v]) => [k, r2(v)]));
    out.available = r2(out.closing - out.restricted - out.fixed_deposit);
    return out;
  })();

  const payload = {
    rows: rows.map((r) => ({ account_id: r.account_id, currency: r.currency, closing_stmt: r.closing })),
    transfers: [],
    tops,
  };
  const dirty = useDirty(payload, onDirty);
  const acctLabel = (r) => <><span className="mono">{r.account_id}</span><div className="cw-sub">{r.bank_name} · {r.account_no}</div></>;
  return (
    <div className="cf-form" id="cf-form">
      <Field1 t={t} totals={totals} />

      <h4 className="cw-h">{t('cashf.field2')}</h4>
      <div className="cs-table-wrap">
        <table className="pa-table cw-table cf-table t1-f2">
          <Field2Head t={t} />
          <tbody>
            {rows.map((r, i) => {
              const c = calc[i];
              const sr = setupRate(r.currency);
              return (
                <tr key={r.account_id} className={c.bad ? 'cw-bad' : ''} data-account={r.account_id}>
                  <td>{acctLabel(r)}</td>
                  <td>{r.currency}</td>
                  <td><NumBox value={r.closing} label={t('xCash.aria.closing', { id: r.account_id })} onChange={(v) => setRow(i, 'closing', v)} /></td>
                  <td className="num cf-calc">{c.restricted ? money(c.restricted) : ''}</td>
                  <td className="num cf-calc">{c.fd ? money(c.fd) : ''}</td>
                  <td className="num cf-calc" data-col="available">{c.available != null ? money(c.available) : ''}</td>
                  <td className="num cf-calc" data-col="rate" title={sr != null ? t('cashf.rateFromSetup') : t('cashf.rateMissing', { ccy: r.currency })}>
                    {sr != null ? sr : <span className="cw-down">—</span>}</td>
                  <td className="cf-center"><SetupTick t={t} on={r.restricted} label={t('xCash.aria.restricted', { id: r.account_id })} /></td>
                  <td className="cf-center"><SetupTick t={t} on={r.fixed_deposit && !r.restricted} label={t('xCash.aria.fixedDeposit', { id: r.account_id })} /></td>
                  <FileCell t={t} kind="statement" account={r.account_id} filesFor={filesFor} fileLink={fileLink} onAttach={onAttach} disabled={busy} />
                  <FileCell t={t} kind="balance" account={r.account_id} filesFor={filesFor} fileLink={fileLink} onAttach={onAttach} disabled={busy} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {['spend', 'payable'].map((kind) => (
        <TopTable key={kind} t={t} kind={kind} rows={tops.filter((x) => x.kind === kind)} categories={form.categories} onChange={setTop(kind)}
          edit={{ amountBox: (x) => <NumBox value={x.amount_sgd} label={t(kind === 'spend' ? 'xCash.aria.spendAmount' : 'xCash.aria.payableAmount', { n: x.rank_no })} onChange={(v) => setTop(kind)(x.rank_no, 'amount_sgd', v)} /> }} />
      ))}
      <p className="cs-hint">{t('cashf.payrollRule')}</p>
      <Bar t={t} busy={busy} onSave={() => onSave(payload)} onCancel={onCancel} dirty={dirty} fresh={!cur} />
    </div>
  );
}

/* The head Tuan's Templates 2 and 3 open with — who the figures are for, and (Template 2) when they
   were prepared and in which currency. Prepare Date is the date of the version in the flow, or today
   for one being typed now. */
function TplHead({ t, rows }) {
  return (
    <table className="t23-head">
      <tbody>
        {rows.map(([k, v]) => <tr key={k}><th>{k}</th><td>{v}</td></tr>)}
      </tbody>
    </table>
  );
}

/* ---- Template 2 — THE MONTHLY CASH PLAN (2026-10-25) --------------------------------------------
   The entity's own sheet (CashPlanSheet.jsx), the yellow cells of the template as boxes: every line for every month,
   the beginning cash (and inventory, where the sheet keeps one) of the first month, and a remark per line. The
   worked-out rows follow while typing, by the same arithmetic as the server. */
export function PlanForm({ t, form, busy, onSave, onCancel, onDirty, entity }) {
  const months = useMemo(() => form.months || [], [form.months]);
  const fmt = form.format || { rows: [], currency: '' };
  const cur = form.current;
  const src = cur || form.prior?.data || null;
  const [begin, setBegin] = useState(() => asText(cur ? cur.begin : form.priorBegin));
  const [invBegin, setInvBegin] = useState(() => asText(cur ? cur.invBegin : form.priorInvBegin));
  const [vals, setVals] = useState(() => {
    const o = {};
    for (const l of src?.lines || []) if (months.includes(l.month)) o[`${l.line_code}|${l.month}`] = asText(l.amount);
    return o;
  });
  const [notes, setNotes] = useState(() => Object.fromEntries((src?.notes || []).map((x) => [x.line_code, x.note || ''])));
  const lines = Object.entries(vals).filter(([, v]) => String(v).trim() !== '').map(([k, amount]) => {
    const [line_code, month] = k.split('|'); return { line_code, month, amount };
  });
  const payload = {
    begin, invBegin: fmt.inventory ? invBegin : undefined, lines,
    notes: Object.entries(notes).filter(([, v]) => String(v).trim() !== '').map(([line_code, note]) => ({ line_code, note })),
  };
  const dirty = useDirty(payload, onDirty);
  const rows = useMemo(() => evalSheet(fmt, months, {
    begin: toNum(begin), invBegin: toNum(invBegin),
    lines: lines.map((l) => ({ ...l, amount: toNum(l.amount) })).filter((l) => l.amount != null && !Number.isNaN(l.amount)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [fmt, months, begin, invBegin, vals]);
  const cell = (r, k) => {
    if (r.t === 'line') {
      const key = `${r.code}|${months[k]}`;
      return <NumBox value={vals[key] ?? ''} label={`${r.label} ${monthLabel(months[k])}`} onChange={(v) => setVals((o) => ({ ...o, [key]: v }))} />;
    }
    if (r.t === 'begin' && k === 0) return <NumBox value={begin} label={t('xCashMp.r_begin')} onChange={setBegin} />;
    if (r.t === 'ibegin' && k === 0) return <NumBox value={invBegin} label={t('xCashMp.beginInv')} onChange={setInvBegin} />;
    return undefined;
  };
  const note = (r) => <input type="text" value={notes[r.code] || ''} aria-label={`${t('xCashMp.h_remarks')} ${r.label}`}
    onChange={(e) => setNotes((o) => ({ ...o, [r.code]: e.target.value }))} />;
  return (
    <div className="cf-form" id="cf-form">
      <SheetTitle entity={entity} format={fmt} />
      {fmt.isDefault && <p className="cs-hint">{t('xCashMp.standardFormat', { entity })}</p>}
      <SheetTable t={t} format={fmt} rows={rows} months={months} cell={cell} note={note} />
      <p className="cs-hint">{t('xCashMp.formBeginHint', { month: monthLabel(months[0]) })}{' '}
        {fmt.unit === 'k' ? `${t('xCashMp.typeWhole', { ccy: fmt.currency })} ` : ''}
        {!cur && (form.prior ? t('xCashMp.formFromPrior', { week: form.prior.week }) : t('xCashMp.formNoPrior'))}</p>
      <Bar t={t} busy={busy} onCancel={onCancel} dirty={dirty} fresh={!cur} onSave={() => onSave(payload)} />
    </div>
  );
}

/* Template 3's head row — "Net-off" over its Inflow and Outflow, as Tuan's sheet groups them; the
   form, the panel's read-only view and the Month-End report all draw it from here. */
export function MonthEndHead({ t, week, tail = null, tail2 = null }) {
  return (
    <thead>
      <tr>
        <th rowSpan={2}>{t('cashw.h_account')}</th><th rowSpan={2}>{t('cashw.h_ccy')}</th>
        <th rowSpan={2} className="num">{t('cashm.h_monthEnd')}</th>
        <th rowSpan={2} className="num">{t('cashm.h_weekEnd', { week })}</th>
        <th rowSpan={2} className="num">{t('cashm.h_variance')}</th>
        <th colSpan={2} className="t3-netoff">{t('cashm.netOff')}</th>
        {tail}
      </tr>
      <tr><th className="num">{t('cashw.tot_inflow')}</th><th className="num">{t('cashw.tot_outflow')}</th>{tail2}</tr>
    </thead>
  );
}

/* ---- Template 3 ------------------------------------------------------------------------------
   TUAN'S "Template 3" SHEET: Entity, then Account · Currency · Month-end Closing · Week-end
   Closing · Variance · Net-off (Inflow · Outflow). The two columns after them come from his
   "Month-end Recconcile" report — Reconcile and Upload Statement — because that is what the
   Preparer checks while typing and where the month's statement is attached. */
export function MonthEndForm({ t, form, busy, onSave, onCancel, onAttach, onDirty, entity, filesFor, fileLink }) {
  const cur = form.current;
  const [lines, setLines] = useState(() => form.accounts.map((a) => {
    const l = cur?.lines?.find((x) => x.account_id === a.account_id);
    return { ...a, month_end_closing: asText(l?.month_end_closing), weekend_closing: asText(l ? l.weekend_closing : form.weekend?.[a.account_id]),
      net_inflow: asText(l?.net_inflow), net_outflow: asText(l?.net_outflow) };
  }));
  const setL = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const payload = { lines: lines.map((l) => ({ account_id: l.account_id, currency: l.currency, month_end_closing: l.month_end_closing,
    weekend_closing: l.weekend_closing, net_inflow: l.net_inflow, net_outflow: l.net_outflow })) };
  const dirty = useDirty(payload, onDirty);
  // Tuan's one formula (2026-10-13): Reconcile = Variance − Inflow + Outflow — the week-end is the last
  // Friday on or before the month-end, so the Net-off flows run from it to the month-end (cashMonthEnd.js).
  const recon = (l) => {
    const [m, w, i, o] = [toNum(l.month_end_closing), toNum(l.weekend_closing), toNum(l.net_inflow) || 0, toNum(l.net_outflow) || 0];
    if ([m, w, i, o].some((x) => Number.isNaN(x)) || m == null || w == null) return null;
    const variance = r2(m - w);
    return { variance, reconcile: r2(variance - i + o) };
  };
  return (
    <div className="cf-form" id="cf-form">
      {/* The month's rule is not repeated here: the period bar above already prints it. */}
      <TplHead t={t} rows={[[t('cashf.entityLabel'), entity || '']]} />
      <table className="pa-table cw-table cf-table t3-recon">
        <MonthEndHead t={t} week={form.rule?.week || ''}
          tail={<><th rowSpan={2}>{t('cashm.h_reconcile')}</th><th rowSpan={2}>{t('cashf.h_upStatement')}</th></>} />
        <tbody>
          {lines.map((l, i) => {
            const rc = recon(l);
            return (
              <tr key={l.account_id} data-account={l.account_id} className={rc && rc.reconcile !== 0 ? 'cw-bad' : ''}>
                <td><span className="mono">{l.account_id}</span><div className="cw-sub">{l.bank_name} · {l.account_no}</div></td>
                <td>{l.currency}</td>
                <td><NumBox value={l.month_end_closing} label={t('xCash.aria.monthEnd', { id: l.account_id })} onChange={(v) => setL(i, 'month_end_closing', v)} /></td>
                <td><NumBox value={l.weekend_closing} label={t('xCash.aria.weekEnd', { id: l.account_id })} onChange={(v) => setL(i, 'weekend_closing', v)} /></td>
                <td className="num cf-calc">{rc ? money(rc.variance) : ''}</td>
                <td><NumBox value={l.net_inflow} label={t('xCash.aria.netInflow', { id: l.account_id })} onChange={(v) => setL(i, 'net_inflow', v)} /></td>
                <td><NumBox value={l.net_outflow} label={t('xCash.aria.netOutflow', { id: l.account_id })} onChange={(v) => setL(i, 'net_outflow', v)} /></td>
                <td className={`cf-check${rc && rc.reconcile === 0 ? ' ok' : ''}${rc && rc.reconcile !== 0 ? ' bad' : ''}`}>
                  {rc ? (rc.reconcile === 0 ? t('cashf.ok') : t('cashf.diff', { d: money(rc.reconcile) })) : ''}
                </td>
                <FileCell t={t} kind="statement" account={l.account_id} filesFor={filesFor} fileLink={fileLink} onAttach={onAttach} disabled={busy} />
              </tr>
            );
          })}
        </tbody>
      </table>
      <Bar t={t} busy={busy} onCancel={onCancel} dirty={dirty} fresh={!cur} onSave={() => onSave(payload)} />
    </div>
  );
}
